<template>
  <div
    id="dev-PixPaymentModal"
    class="pix-payment-modal"
  >
    <div class="pix-modal-content">
      <div class="pix-modal-header">
        <h5 class="modal-title">
          {{ $t('pix_payment.title') || 'Pagar com PIX' }}
        </h5>
        <ModalCloseButton @close="$emit('close')" />
      </div>

      <div class="pix-modal-body">
        <!-- Countdown Timer -->
        <div
          v-if="!hasExpired && pixCreatedAt && !paymentConfirmed"
          class="pix-countdown"
          :class="timerStateClass"
        >
          <div class="countdown-display">
            <font-awesome-icon
              icon="clock"
              class="countdown-icon"
            />
            <span class="countdown-label">{{ $t('checkout.pix_expires_in') }}</span>
            <span class="countdown-time">{{ formattedTime }}</span>
          </div>
          <p
            v-if="timerUrgencyMessage"
            class="countdown-message"
          >
            {{ timerUrgencyMessage }}
          </p>
        </div>

        <!-- Expired Banner -->
        <div
          v-if="hasExpired"
          class="pix-expired-banner"
        >
          <div class="expired-icon-wrapper">
            <font-awesome-icon
              icon="circle-exclamation"
              class="expired-icon"
            />
          </div>
          <span class="expired-title">
            {{ $t('checkout.pix_qr_expired') || 'QR Code expirado' }}
          </span>
          <p class="expired-message">
            {{
              $t('checkout.pix_qr_expired_message') ||
                'O tempo para pagamento expirou. Gere um novo QR Code para continuar.'
            }}
          </p>
        </div>

        <!-- Alert Info -->
        <div
          v-if="!hasExpired && !paymentConfirmed"
          class="pix-alert"
        >
          <div class="alert-header">
            <font-awesome-icon
              icon="circle-info"
              class="alert-icon"
            />
            <span class="alert-title">{{ $t('checkout.pix_important_info') || 'Informação Importante' }}</span>
          </div>
          <p class="alert-message">
            {{
              $t('checkout.pix_return_to_website') || 'Efetue o pagamento e retorne aqui para prosseguir com o aluguel'
            }}
          </p>
        </div>

        <div
          v-if="!hasExpired && !paymentConfirmed"
          class="pix-instruction-container"
        >
          <p class="pix-instruction">
            {{ $t('checkout.pix_scan_or_copy') }}
          </p>
        </div>

        <!-- QR Code -->
        <div
          v-if="!paymentConfirmed"
          class="qr-code-placeholder"
        >
          <div
            v-if="loading && !paymentConfirmed && !hasExpired"
            class="qr-code-loading"
          >
            <div class="spinner-border"></div>
            <p>{{ $t('checkout.generating_qr_code') }}</p>
          </div>
          <div
            v-else-if="qrCode && !hasExpired"
            class="qr-code-container"
          >
            <img
              :src="qrCode"
              alt="PIX QR Code"
              class="qr-code-image"
            >
          </div>
          <div
            v-else-if="hasExpired"
            class="qr-code-expired-state"
          >
            <font-awesome-icon
              icon="qrcode"
              class="expired-qr-icon"
            />
            <BaseButton
              variant="primary"
              size="sm"
              class="mt-3 regenerate-btn"
              :disabled="loading"
              @click="handleGenerateNewQr"
            >
              <span
                v-if="loading"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              {{ $t('checkout.pix_generate_new_qr') || 'Gerar novo QR Code' }}
            </BaseButton>
          </div>
          <div
            v-else
            class="qr-code-error"
          >
            <p>{{ $t('checkout.pix_error_generating') }}</p>
            <BaseButton
              variant="outline-primary"
              size="sm"
              class="mt-2"
              @click="$emit('retry')"
            >
              Tentar Novamente
            </BaseButton>
          </div>
        </div>

        <!-- Copy Paste Key -->
        <div
          v-if="copyPasteKey && !hasExpired && !paymentConfirmed"
          class="pix-copy-paste"
        >
          <label>{{ $t('checkout.pix_copy_paste_key') }}</label>
          <div class="copy-paste-input">
            <input
              type="text"
              readonly
              :value="copyPasteKey"
              class="form-control selectable-input"
              @click="$event.target.select()"
            >
            <BaseButton
              bare
              variant=""
              class="btn-copy"
              @click="copyPixKey"
            >
              <font-awesome-icon icon="copy" />
              {{ copyButtonText }}
            </BaseButton>
          </div>
        </div>

        <div class="payment-status-container">
          <div
            v-if="!paymentConfirmed && !hasExpired"
            class="status-waiting"
          >
            <div class="spinner-border spinner-border-sm me-2 text-warning"></div>
            <span class="payment-status-text">
              {{ $t('checkout.waiting_payment') }}
            </span>
          </div>

          <div
            v-else-if="paymentConfirmed"
            class="withdrawal-section"
          >
            <div class="success-badge mb-3">
              <span
                v-if="isProcessing"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              <font-awesome-icon
                v-else
                icon="circle-check"
                class="me-2"
              />
              <span>{{
                isProcessing ? $t('checkout.processing') || 'Processando...' : $t('checkout.payment_confirmed')
              }}</span>
            </div>
            <div class="d-flex justify-content-center">
              <BaseButton
                variant="primary"
                constrained
                class="font-weight-bold"
                :disabled="isProcessing"
                @click="$emit('take-battery')"
              >
                <span
                  v-if="isProcessing"
                  class="spinner-border spinner-border-sm me-2"
                ></span>
                {{ $t('checkout.take_battery') || 'Retirar Bateria' }}
              </BaseButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import BaseButton from '@/components/ui/BaseButton.vue'
  import ModalCloseButton from '@/components/ui/ModalCloseButton.vue'

  export default {
    name: 'PixPaymentModal',
    components: {
      BaseButton,
      ModalCloseButton
    },
    props: {
      qrCode: {
        type: String,
        default: null
      },
      copyPasteKey: {
        type: String,
        default: null
      },
      loading: {
        type: Boolean,
        default: false
      },
      paymentConfirmed: {
        type: Boolean,
        default: false
      },
      isProcessing: {
        type: Boolean,
        default: false
      },
      pixCreatedAt: {
        type: Number,
        default: null
      },
      pixExpiryMinutes: {
        type: Number,
        default: 10
      }
    },
    emits: ['close', 'retry', 'take-battery', 'expired', 'generate-new-qr'],
    data() {
      return {
        copyButtonText: 'Copiar',
        remainingSeconds: 0,
        countdownInterval: null,
        hasExpired: false
      }
    },
    computed: {
      formattedTime() {
        const mins = Math.floor(this.remainingSeconds / 60)
        const secs = this.remainingSeconds % 60
        return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
      },
      timerStateClass() {
        if (this.remainingSeconds <= 60) return 'timer-danger'
        if (this.remainingSeconds <= 120) return 'timer-warning'
        return 'timer-normal'
      },
      timerUrgencyMessage() {
        if (this.remainingSeconds <= 60) {
          return this.$t('checkout.pix_expiring_soon')
        }
        if (this.remainingSeconds <= 120) {
          return this.$t('checkout.pix_expiring_warning')
        }
        return ''
      }
    },
    watch: {
      pixCreatedAt() {
        this.startCountdown()
      },
      paymentConfirmed(val) {
        if (val) {
          this.clearCountdown()
        }
      }
    },
    mounted() {
      if (this.pixCreatedAt) {
        this.startCountdown()
      }
    },
    beforeUnmount() {
      this.clearCountdown()
    },
    methods: {
      copyPixKey() {
        if (!this.copyPasteKey) return

        navigator.clipboard
          .writeText(this.copyPasteKey)
          .then(() => {
            this.copyButtonText = 'Copiado!'
            setTimeout(() => {
              this.copyButtonText = 'Copiar'
            }, 2000)
          })
          .catch(err => {
            console.error('Error copying to clipboard:', err)
            this.fallbackCopyToClipboard()
          })
      },

      fallbackCopyToClipboard() {
        const textArea = document.createElement('textarea')
        textArea.value = this.copyPasteKey
        textArea.style.position = 'absolute'
        textArea.style.left = '-9999px'
        textArea.style.top = '0'
        textArea.setAttribute('readonly', '')
        document.body.appendChild(textArea)

        try {
          if (window.navigator.userAgent.match(/ipad|iphone/i)) {
            const range = document.createRange()
            range.selectNodeContents(textArea)
            const selection = window.getSelection()
            selection.removeAllRanges()
            selection.addRange(range)
            textArea.setSelectionRange(0, 999999)
          } else {
            textArea.select()
          }

          document.execCommand('copy')
          this.copyButtonText = 'Copiado!'
          setTimeout(() => {
            this.copyButtonText = 'Copiar'
          }, 2000)
        } catch (err) {
          console.error('Fallback copy error:', err)
          this.copyButtonText = 'Erro ao copiar'
        } finally {
          document.body.removeChild(textArea)
        }
      },

      startCountdown() {
        this.clearCountdown()
        this.hasExpired = false

        if (!this.pixCreatedAt) return

        const expiryTimestamp = this.pixCreatedAt + this.pixExpiryMinutes * 60 * 1000
        const remaining = Math.max(0, Math.floor((expiryTimestamp - Date.now()) / 1000))
        this.remainingSeconds = remaining

        if (remaining <= 0) {
          this.hasExpired = true
          this.$emit('expired')
          return
        }

        if (this.paymentConfirmed) {
          return
        }

        // Recalculate remaining time from pixCreatedAt on each tick
        // to avoid timer drift when the browser throttles setInterval
        // (e.g. when the tab is backgrounded or phone screen is locked)
        this.countdownInterval = setInterval(() => {
          const elapsedMs = Date.now() - this.pixCreatedAt
          const totalMs = this.pixExpiryMinutes * 60 * 1000
          const remaining = Math.max(0, Math.ceil((totalMs - elapsedMs) / 1000))
          this.remainingSeconds = remaining
          if (remaining <= 0) {
            this.clearCountdown()
            this.hasExpired = true
            this.$emit('expired')
          }
        }, 1000)
      },

      clearCountdown() {
        if (this.countdownInterval) {
          clearInterval(this.countdownInterval)
          this.countdownInterval = null
        }
      },

      handleGenerateNewQr() {
        this.$emit('generate-new-qr')
      }
    }
  }
</script>

<style scoped lang="scss">
  $color-energy-orange: #f58a48;

  .pix-payment-modal {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    z-index: 10000; /* Higher than SideBar (z-index: 1000) */
    display: flex;
    align-items: flex-end; /* Mobile-first: sheet from bottom */
    justify-content: center;
    padding: 0;
  }

  .pix-modal-content {
    background: white;
    border-radius: 20px 20px 0 0; /* Rounded top for bottom sheet */
    width: 100%;
    max-width: 450px;
    max-height: calc(var(--app-viewport-safe-height, 100vh) - 16px);
    max-height: calc(var(--app-viewport-safe-height, 100dvh) - 16px);
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: slide-up 0.3s cubic-bezier(0.175, 0.885, 0.32, 1);
    position: relative;

    /* Pull handle indicator */
    &::before {
      content: '';
      width: 40px;
      height: 4px;
      background-color: #ddd;
      border-radius: 2px;
      position: absolute;
      top: 10px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 1;
    }
  }

  @keyframes slide-up {
    from {
      opacity: 0;
      transform: translateY(100%);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .pix-modal-header {
    padding: 1.25rem 1.25rem 1rem; /* Added top padding for pull handle */
    border-bottom: 1px solid #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .modal-title {
      margin: 0;
      font-size: 1.1rem;
      font-weight: 800;
      color: #2c3e50;
      flex: 1;
      text-align: center;
    }
  }

  .pix-modal-body {
    padding: 1.5rem;
    max-height: calc(85vh - 80px); /* Account for header height */
    overflow-y: auto;
  }

  /* Countdown Timer */
  .pix-countdown {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0.6rem 0.85rem;
    margin-bottom: 0.75rem;
    border-radius: 12px;
    transition: all 0.3s ease;

    &.timer-normal {
      background: #eff6ff;
      border: 1px solid #bfdbfe;

      .countdown-icon,
      .countdown-label,
      .countdown-time {
        color: #1d4ed8;
      }
    }

    &.timer-warning {
      background: #fff7ed;
      border: 1px solid #fed7aa;
      box-shadow: 0 0 0 2px rgba(251, 146, 60, 0.15);

      .countdown-icon,
      .countdown-label,
      .countdown-time {
        color: #c2410c;
      }
    }

    &.timer-danger {
      background: #fef2f2;
      border: 1px solid #fecaca;
      box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.15);
      animation: pulse 1s ease-in-out infinite;

      .countdown-icon,
      .countdown-label,
      .countdown-time {
        color: #dc2626;
      }
    }
  }

  @keyframes pulse {
    0%,
    100% {
      box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.15);
    }
    50% {
      box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.3);
    }
  }

  .countdown-display {
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }

  .countdown-icon {
    font-size: 0.85rem;
  }

  .countdown-label {
    font-size: 0.8rem;
    font-weight: 600;
    color: inherit;
    white-space: nowrap;
  }

  .countdown-time {
    font-size: 1.2rem;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.02em;
  }

  .countdown-message {
    margin: 0.15rem 0 0;
    font-size: 0.65rem;
    font-weight: 600;
    color: #6b7280;
    text-align: center;
  }

  /* Expired Banner */
  .pix-expired-banner {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1.25rem 1rem;
    margin-bottom: 1rem;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 14px;
    text-align: center;

    .expired-icon-wrapper {
      margin-bottom: 0.5rem;
    }

    .expired-icon {
      font-size: 1.5rem;
      color: #dc2626;
    }

    .expired-title {
      font-size: 1rem;
      font-weight: 800;
      color: #991b1b;
      margin-bottom: 0.25rem;
    }

    .expired-message {
      font-size: 0.8rem;
      color: #7f1d1d;
      margin: 0;
      font-weight: 500;
      line-height: 1.4;
    }
  }

  .pix-alert {
    background: #fff9f0;
    border: 1px solid #ffecb3;
    border-radius: 12px;
    padding: 1rem;
    margin-bottom: 1.5rem;

    .alert-header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 0.4rem;
    }

    .alert-icon {
      color: $color-energy-orange;
      font-size: 0.9rem;
    }

    .alert-title {
      font-weight: 800;
      color: #854d0e;
      font-size: 0.75rem;
      text-transform: uppercase;
    }

    .alert-message {
      color: #92400e;
      font-size: 0.8rem;
      margin: 0;
      line-height: 1.4;
      font-weight: 600;
    }
  }

  .pix-instruction-container {
    text-align: center;
    margin-bottom: 1rem;
  }

  .pix-instruction {
    font-size: 0.9rem;
    color: #4b5563;
    font-weight: 700;
  }

  .qr-code-placeholder {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
    background: #f9fafb;
    border: 2px dashed #e5e7eb;
    border-radius: 16px;
    margin-bottom: 1.5rem;
    padding: 1rem;
  }

  .qr-code-image {
    max-width: 100%;
    max-height: 180px;
  }

  .qr-code-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;

    .spinner-border {
      color: $color-energy-orange;
      width: 2.5rem;
      height: 2.5rem;
    }

    p {
      margin: 0;
      font-size: 0.85rem;
      color: #6b7280;
      font-weight: 600;
    }
  }

  .qr-code-expired-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 1rem;

    .expired-qr-icon {
      font-size: 3rem;
      color: #d1d5db;
    }
  }

  .regenerate-btn {
    font-weight: 700;
  }

  .pix-copy-paste {
    margin-bottom: 1.5rem;

    label {
      font-size: 0.75rem;
      font-weight: 800;
      color: #374151;
      margin-bottom: 0.5rem;
      display: block;
      text-transform: uppercase;
    }
  }

  .copy-paste-input {
    display: flex;
    gap: 0.5rem;

    .form-control {
      border-radius: 10px;
      background: #f3f4f6;
      border: 1px solid #e5e7eb;
      font-size: 0.8rem;
      height: 42px;
    }

    .btn-copy {
      background: white;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 0 1rem;
      font-size: 0.8rem;
      font-weight: 700;
      color: #374151;
      height: 42px;

      &:hover {
        background: #f9fafb;
        border-color: #d1d5db;
      }
    }
  }

  .payment-status-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 0.5rem;
  }

  .status-waiting {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.75rem;
    background: #fefce8;
    border-radius: 10px;
    width: 100%;
    border: 1px solid #fef08a;
  }

  .payment-status-text {
    font-size: 0.85rem;
    color: #854d0e;
    font-weight: 700;
  }

  .withdrawal-section {
    width: 100%;
    display: flex;
    flex-direction: column;
  }

  .success-badge {
    background: #ecfdf5;
    color: #059669;
    padding: 1rem;
    border-radius: 12px;
    text-align: center;
    font-weight: 800;
    font-size: 1rem;
    border: 1px solid #d1fae5;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  @media (max-width: 480px) {
    .pix-modal-content {
      max-width: 100%;
    }
  }
</style>
